import { Component, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../material/material.module';
import { ActiveFilter, FilterSummary } from '../../advanced-search/models/search-state.models';
import { slideIn, chipAnimation, counterAnimation, fadeIn } from '../../advanced-search/shared/animations';

/**
 * ActiveFiltersCounterComponent
 * 
 * Exibe o contador de filtros ativos e permite visualizar resumo.
 * 
 * Requisitos: 11.3 - Animar adição/remoção de chips
 * Requisitos: 11.4 - Animar atualização do contador de filtros
 * 
 * @example
 * ```html
 * <app-active-filters-counter
 *   [filters]="activeFilters"
 *   (filterRemoved)="onFilterRemoved($event)"
 *   (filterClicked)="onFilterClicked($event)"
 *   (clearAllClicked)="onClearAllFilters()">
 * </app-active-filters-counter>
 * ```
 */
@Component({
  selector: 'app-active-filters-counter',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './active-filters-counter.component.html',
  styleUrl: './active-filters-counter.component.scss',
  animations: [slideIn, chipAnimation, counterAnimation, fadeIn]
})
export class ActiveFiltersCounterComponent {
  // Inputs
  filters = input.required<ActiveFilter[]>();
  
  // Outputs
  filterRemoved = output<ActiveFilter>();
  filterClicked = output<string>();
  clearAllClicked = output<void>();
  
  // State
  showSummary = signal(false);
  
  // Computed
  activeFiltersCount = computed(() => this.filters().length);
  
  categories = computed(() => {
    const filtersList = this.filters();
    const categorySet = new Set(filtersList.map(f => f.category));
    return Array.from(categorySet);
  });
  
  filterSummary = computed<FilterSummary>(() => {
    const filtersList = this.filters();
    const grouped = new Map<string, ActiveFilter[]>();
    
    filtersList.forEach(filter => {
      if (!grouped.has(filter.category)) {
        grouped.set(filter.category, []);
      }
      grouped.get(filter.category)!.push(filter);
    });
    
    return {
      totalCount: filtersList.length,
      filters: filtersList,
      groupedByCategory: grouped
    };
  });
  
  /**
   * Alterna a visibilidade do painel de resumo
   */
  toggleSummaryPanel(): void {
    this.showSummary.update(value => !value);
  }
  
  /**
   * Retorna filtros para uma categoria específica
   */
  getFiltersForCategory(category: string): ActiveFilter[] {
    return this.filterSummary().groupedByCategory.get(category) || [];
  }
  
  /**
   * Remove um filtro específico
   */
  clearFilter(filter: ActiveFilter): void {
    this.filterRemoved.emit(filter);
  }
  
  /**
   * Navega para o campo do filtro
   */
  navigateToFilter(fieldName: string): void {
    this.filterClicked.emit(fieldName);
  }
  
  /**
   * Limpa todos os filtros
   */
  clearAllFilters(): void {
    this.clearAllClicked.emit();
  }
}
